import React, {useState} from 'react'
import {motion} from 'framer-motion'
import {Link} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import SafeIcon from '../common/SafeIcon'
import LoadingSpinner from '../components/ui/LoadingSpinner'
import toast from 'react-hot-toast'
import * as FiIcons from 'react-icons/fi'

const {FiArrowLeft, FiMail, FiUser, FiMessageSquare, FiSend, FiMapPin, FiClock, FiPhone} = FiIcons

const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const {register, handleSubmit, reset, formState: {errors}} = useForm()

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      // Create mailto URL with form data
      const subject = encodeURIComponent(`Contact Form: ${data.subject || 'General Inquiry'}`)
      const body = encodeURIComponent(`
Name: ${data.name}
Email: ${data.email}
Subject: ${data.subject || 'General Inquiry'}

Message:
${data.message}

---
Sent from Empower Nutrition Contact Form
      `.trim())

      const mailtoUrl = `mailto:laura@empowernutrition.info?subject=${subject}&body=${body}`

      // Open email client
      window.location.href = mailtoUrl

      // Show success message and reset form
      toast.success('Email client opened! Your message has been prepared.')
      reset()
    } catch (error) {
      console.error('Error opening email client:', error)
      toast.error('There was an issue opening your email client. Please email us directly at laura@empowernutrition.info')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center space-x-2 text-nutrition-600 hover:text-nutrition-700 mb-8 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{opacity: 0, x: -50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8}}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
          >
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Get in Touch</h1>
              <p className="text-gray-600">
                Have questions about our courses or need personalized nutrition guidance? We'd love to hear from you!
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    {...register('name', {
                      required: 'Name is required',
                      minLength: {value: 2, message: 'Name must be at least 2 characters'}
                    })}
                    type="text"
                    id="name"
                    className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="Enter your full name"
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                    type="email"
                    id="email"
                    className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="Enter your email address"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Subject Field */}
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  Subject
                </label>
                <select
                  {...register('subject')}
                  id="subject"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                >
                  <option value="">Select a topic (optional)</option>
                  <option value="Course Information">Course Information</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Billing Question">Billing Question</option>
                  <option value="Nutrition Consultation">Nutrition Consultation</option>
                  <option value="Partnership Inquiry">Partnership Inquiry</option>
                  <option value="Media Inquiry">Media Inquiry</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMessageSquare} className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <textarea
                    {...register('message', {
                      required: 'Message is required',
                      minLength: {value: 10, message: 'Message must be at least 10 characters'}
                    })}
                    id="message"
                    rows={6}
                    className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500 resize-none"
                    placeholder="Tell us how we can help you..."
                  />
                </div>
                {errors.message && (
                  <p className="mt-1 text-sm text-red-600">{errors.message.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                    <span>Send Message</span>
                  </>
                )}
              </button>

              <p className="text-sm text-gray-500 text-center">
                We typically respond within 24-48 hours during business days.
              </p>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{opacity: 0, x: 50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="space-y-8"
          >
            {/* Contact Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-nutrition-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiMail} className="w-6 h-6 text-nutrition-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600 mb-1">
                      <a
                        href="mailto:laura@empowernutrition.info"
                        className="text-nutrition-600 hover:text-nutrition-700"
                      >
                        laura@empowernutrition.info
                      </a>
                    </p>
                    <p className="text-sm text-gray-500">
                      We respond within 24-48 hours
                    </p>
                  </div>
                </div>

                {/* Response Time */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-nutrition-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiClock} className="w-6 h-6 text-nutrition-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Response Time</h3>
                    <p className="text-gray-600 mb-1">24-48 hours</p>
                    <p className="text-sm text-gray-500">
                      Monday - Friday, 9 AM - 5 PM EST
                    </p>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-nutrition-100 rounded-lg flex items-center justify-center">
                    <SafeIcon icon={FiMapPin} className="w-6 h-6 text-nutrition-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Location</h3>
                    <p className="text-gray-600 mb-1">United States</p>
                    <p className="text-sm text-gray-500">
                      Serving clients worldwide
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-br from-nutrition-600 to-nutrition-700 rounded-xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">Need Immediate Help?</h3>
              <p className="text-nutrition-100 mb-6">
                Book a consultation call to discuss your nutrition goals and see how our courses can help you achieve lasting results.
              </p>
              <div className="space-y-4">
                <a
                  href="https://tidycal.com/empowernutrition/30-minute-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-white text-nutrition-600 hover:bg-gray-100 py-3 px-6 rounded-lg font-semibold transition-colors text-center"
                >
                  Book a Consultation
                </a>
                <Link
                  to="/courses"
                  className="block w-full border-2 border-white text-white hover:bg-white hover:text-nutrition-600 py-3 px-6 rounded-lg font-semibold transition-colors text-center"
                >
                  Browse Our Courses
                </Link>
              </div>
            </div>

            {/* FAQ Link */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Frequently Asked Questions</h3>
              <p className="text-gray-600 mb-4">
                Check out our FAQ section for quick answers to common questions about our courses, nutrition approach, and platform features.
              </p>
              <Link
                to="/#faq"
                className="text-nutrition-600 hover:text-nutrition-700 font-medium flex items-center space-x-1"
              >
                <span>View FAQ</span>
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 rotate-180" />
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Contact